
import React, { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ContentManagementGrid } from "@/components/dashboard/ContentManagementGrid";
import { AnalyticsDashboard } from "@/components/dashboard/AnalyticsDashboard";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format, subDays } from "date-fns";

interface Webnovel {
  id: string;
  title: string;
  cover_url: string;
  created_at: string;
  archived: boolean;
}

interface AnalyticsData {
  date: string;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  sales: number;
}

const MyWebnovels = () => {
  const navigate = useNavigate();
  const [webnovels, setWebnovels] = useState<Webnovel[]>([]);
  const [activeWebnovels, setActiveWebnovels] = useState<Webnovel[]>([]);
  const [archivedWebnovels, setArchivedWebnovels] = useState<Webnovel[]>([]);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Fetch webnovels data from Supabase
  const fetchWebnovels = async () => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/auth');
        return;
      }
      
      const { data, error } = await supabase
        .from('webnovels')
        .select('*')
        .eq('author_id', session.user.id)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      if (data) {
        setWebnovels(data);
        setActiveWebnovels(data.filter(webnovel => !webnovel.archived));
        setArchivedWebnovels(data.filter(webnovel => webnovel.archived));
      }
    } catch (error) {
      console.error('Error fetching webnovels:', error);
      toast.error('Erro ao carregar seus webnovels');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch analytics data
  const fetchAnalytics = async (startDate: Date, endDate: Date) => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/auth');
        return;
      }
      
      const { data: webnovels } = await supabase
        .from('webnovels')
        .select('id')
        .eq('author_id', session.user.id);
      
      if (!webnovels || webnovels.length === 0) {
        setAnalyticsData([]);
        setIsLoading(false);
        return;
      }
      
      const webnovelIds = webnovels.map(webnovel => webnovel.id);
      
      const { data, error } = await supabase
        .from('webnovel_analytics')
        .select('*')
        .in('webnovel_id', webnovelIds)
        .gte('date', format(startDate, 'yyyy-MM-dd'))
        .lte('date', format(endDate, 'yyyy-MM-dd'))
        .order('date', { ascending: true });
        
      if (error) throw error;
      
      // Process analytics data by date
      const processedData: Record<string, AnalyticsData> = {};
      
      // Initialize all dates in the range
      let currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dateStr = format(currentDate, 'yyyy-MM-dd');
        processedData[dateStr] = {
          date: format(currentDate, 'dd/MM'),
          views: 0,
          likes: 0,
          comments: 0,
          shares: 0,
          sales: 0
        };
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Fill with actual data
      if (data) {
        data.forEach(item => {
          const dateStr = format(new Date(item.date), 'yyyy-MM-dd');
          const displayDate = format(new Date(item.date), 'dd/MM');
          
          if (processedData[dateStr]) {
            processedData[dateStr].views += item.views;
            processedData[dateStr].likes += item.likes;
            processedData[dateStr].comments += item.comments;
            processedData[dateStr].shares += item.shares;
            processedData[dateStr].sales += item.sales;
          } else {
            processedData[dateStr] = {
              date: displayDate,
              views: item.views,
              likes: item.likes,
              comments: item.comments,
              shares: item.shares,
              sales: item.sales
            };
          }
        });
      }
      
      // Convert to array for the chart
      setAnalyticsData(Object.values(processedData));
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Erro ao carregar dados de análise');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle archive action
  const handleArchive = async (id: string) => {
    try {
      const webnovel = webnovels.find(w => w.id === id);
      if (!webnovel) return;
      
      const { error } = await supabase
        .from('webnovels')
        .update({ archived: !webnovel.archived })
        .eq('id', id);
        
      if (error) throw error;
      
      await fetchWebnovels();
    } catch (error) {
      console.error('Error archiving webnovel:', error);
      throw error;
    }
  };
  
  // Handle delete action
  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('webnovels')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      await fetchWebnovels();
    } catch (error) {
      console.error('Error deleting webnovel:', error);
      throw error;
    }
  };
  
  // Handle edit action
  const handleEdit = (id: string) => {
    navigate(`/edit/webnovel/${id}`);
  };
  
  // Handle add chapter action
  const handleAddChapter = (id: string) => {
    navigate(`/create/webnovel/chapter/${id}`);
  };
  
  // Handle date range change
  const handleDateRangeChange = (startDate: Date, endDate: Date) => {
    fetchAnalytics(startDate, endDate);
  };
  
  useEffect(() => {
    fetchWebnovels();
    
    // Initial analytics fetch - last 7 days
    const endDate = new Date();
    const startDate = subDays(endDate, 7);
    fetchAnalytics(startDate, endDate);
  }, []);
  
  return (
    <DashboardLayout title="Gerenciamento de Webnovels">
      <Tabs defaultValue="content">
        <TabsList className="mb-6">
          <TabsTrigger value="content">Conteúdo</TabsTrigger>
          <TabsTrigger value="analytics">Análises</TabsTrigger>
          <TabsTrigger value="archived">Arquivados</TabsTrigger>
        </TabsList>
        
        <TabsContent value="content" className="mt-0">
          <ContentManagementGrid
            items={activeWebnovels}
            contentType="webnovel"
            onArchive={handleArchive}
            onDelete={handleDelete}
            onEdit={handleEdit}
            onAddChapter={handleAddChapter}
            refreshData={fetchWebnovels}
          />
        </TabsContent>
        
        <TabsContent value="analytics" className="mt-0">
          <AnalyticsDashboard
            data={analyticsData}
            contentType="webnovel"
            onDateRangeChange={handleDateRangeChange}
            isLoading={isLoading}
          />
        </TabsContent>
        
        <TabsContent value="archived" className="mt-0">
          <ContentManagementGrid
            items={archivedWebnovels}
            contentType="webnovel"
            onArchive={handleArchive}
            onDelete={handleDelete}
            onEdit={handleEdit}
            onAddChapter={handleAddChapter}
            refreshData={fetchWebnovels}
          />
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
};

export default MyWebnovels;
